import React, { useMemo } from 'react';
import { Search, Bell, Menu, Sun, Moon, User, LogOut } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
    onMenuClick: () => void;
    searchTerm: string;
    onSearchChange: (value: string) => void;
    ownerName: string;
    onLogout: () => void;
    companyName?: string;
    userRole?: string;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick, searchTerm, onSearchChange, ownerName, onLogout, companyName, userRole }) => {
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [isNotificationsOpen, setIsNotificationsOpen] = React.useState(false);
    const [isProfileOpen, setIsProfileOpen] = React.useState(false);
    const [isSearchFocused, setIsSearchFocused] = React.useState(false);

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(part => part[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Memoize notifications array to prevent recreation on every render
    const notifications = useMemo(() => [
        { id: 1, title: 'New Upload', message: 'John Doe uploaded an ESA letter', time: '2m ago', unread: true },
        { id: 2, title: 'Verification Complete', message: 'Review for Unit 402 is ready', time: '1h ago', unread: true },
        { id: 3, title: 'System Update', message: 'Platform maintenance scheduled', time: '5h ago', unread: false },
    ], []);

    // Memoize unreadCount to prevent recalculation on every render
    const unreadCount = useMemo(() => notifications.filter(n => n.unread).length, [notifications]);

    return (
        <header className={`sticky top-0 z-30 flex items-center justify-between px-6 py-4 border-b backdrop-blur-md transition-colors duration-300 ${theme === 'dark'
            ? 'bg-storgia-black/80 border-storgia-light-blurple/20'
            : 'bg-white/80 border-gray-200'
            }`}>
            <div className="flex items-center gap-4 flex-1">
                <button
                    type="button"
                    onClick={onMenuClick}
                    aria-label="Toggle navigation menu"
                    className={`p-2 rounded-lg lg:hidden ${theme === 'dark' ? 'hover:bg-storgia-light-blurple/10 text-white' : 'hover:bg-gray-100 text-gray-600'
                        }`}
                >
                    <Menu className="h-6 w-6" aria-hidden="true" />
                </button>

                <div className={`relative transition-all duration-300 ease-in-out hidden sm:block ${isSearchFocused ? 'max-w-xl w-full' : 'max-w-md w-full'
                    }`}>
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors ${isSearchFocused
                        ? theme === 'dark' ? 'text-storgia-blurple' : 'text-indigo-600'
                        : theme === 'dark' ? 'text-storgia-light-blurple/50' : 'text-gray-400'
                        }`} />
                    <input
                        type="text"
                        aria-label="Search properties, residents, or documents"
                        placeholder="Search properties, residents, or documents..."
                        value={searchTerm}
                        onChange={(e) => onSearchChange(e.target.value)}
                        onFocus={() => setIsSearchFocused(true)}
                        onBlur={() => setIsSearchFocused(false)}
                        className={`w-full pl-10 pr-4 py-2.5 rounded-xl border font-league-spartan focus:outline-none transition-all duration-300 ${theme === 'dark'
                            ? 'bg-storgia-dark border-storgia-light-blurple/20 text-white placeholder:text-storgia-light-blurple/50 focus:ring-2 focus:ring-storgia-blurple focus:border-transparent shadow-inner'
                            : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm'
                            }`}
                    />
                </div>
            </div>

            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={toggleTheme}
                    aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                    className={`p-2 rounded-full transition-all duration-200 hover:scale-110 ${theme === 'dark'
                        ? 'hover:bg-storgia-light-blurple/10 text-storgia-light-blurple'
                        : 'hover:bg-gray-100 text-gray-600'
                        }`}
                >
                    {theme === 'dark' ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
                </button>

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                        aria-label={
                            isNotificationsOpen
                                ? 'Close notifications'
                                : `Open notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`
                        }
                        aria-expanded={isNotificationsOpen}
                        aria-haspopup="menu"
                        className={`p-2 rounded-full transition-all duration-200 hover:scale-110 relative ${theme === 'dark'
                            ? 'hover:bg-storgia-light-blurple/10 text-storgia-light-blurple'
                            : 'hover:bg-gray-100 text-gray-600'
                            } ${isNotificationsOpen ? (theme === 'dark' ? 'bg-storgia-light-blurple/10 text-white' : 'bg-gray-100 text-gray-900') : ''}`}
                    >
                        <Bell className="h-5 w-5" aria-hidden="true" />
                        {unreadCount > 0 && (
                            <span
                                className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-storgia-pink animate-pulse shadow-[0_0_8px_rgba(236,72,153,0.6)]"
                                aria-hidden="true"
                            />
                        )}
                    </button>

                    {/* Notification Dropdown */}
                    {isNotificationsOpen && (
                        <>
                            <button
                                type="button"
                                className="fixed inset-0 z-40 cursor-default"
                                onClick={() => setIsNotificationsOpen(false)}
                                aria-label="Close notifications"
                            />
                            <div className={`absolute right-0 mt-3 w-80 rounded-2xl shadow-2xl border z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 ${theme === 'dark'
                                ? 'bg-storgia-dark border-storgia-light-blurple/20'
                                : 'bg-white border-gray-100'
                                }`}>
                                <div className={`px-4 py-3 border-b flex justify-between items-center ${theme === 'dark' ? 'border-storgia-light-blurple/10 bg-storgia-blurple/5' : 'border-gray-50 bg-gray-50/50'}`}>
                                    <h3 className={`font-bold font-league-spartan text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Notifications</h3>
                                    {unreadCount > 0 && (
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${theme === 'dark' ? 'bg-storgia-pink/20 text-storgia-pink' : 'bg-pink-100 text-pink-600'}`}>
                                            {unreadCount} New
                                        </span>
                                    )}
                                </div>
                                <div className="max-h-[300px] overflow-y-auto">
                                    {notifications.map((notif) => (
                                        <button
                                            key={notif.id}
                                            type="button"
                                            className={`w-full text-left p-4 border-b last:border-0 hover:bg-opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-inset ${theme === 'dark'
                                                ? 'border-storgia-light-blurple/5 hover:bg-storgia-light-blurple/5 focus:ring-storgia-blurple'
                                                : 'border-gray-50 hover:bg-gray-50 focus:ring-indigo-500'
                                                } ${notif.unread ? (theme === 'dark' ? 'bg-storgia-blurple/5' : 'bg-indigo-50/30') : ''}`}
                                        >
                                            <div className="flex justify-between items-start mb-1">
                                                <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{notif.title}</span>
                                                <span className={`text-[10px] ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>{notif.time}</span>
                                            </div>
                                            <p className={`text-xs leading-relaxed ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{notif.message}</p>
                                        </button>
                                    ))}
                                </div>
                                <div className={`p-2 text-center border-t ${theme === 'dark' ? 'border-storgia-light-blurple/10' : 'border-gray-50'}`}>
                                    <button className={`text-xs font-medium hover:underline ${theme === 'dark' ? 'text-storgia-light-blurple' : 'text-indigo-600'}`}>
                                        Mark all as read
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        aria-label={isProfileOpen ? 'Close profile menu' : 'Open profile menu'}
                        aria-expanded={isProfileOpen}
                        aria-haspopup="menu"
                        className={`h-9 w-9 rounded-full flex items-center justify-center font-bold font-league-spartan shadow-lg transition-transform hover:scale-105 ${theme === 'dark'
                            ? 'bg-gradient-to-br from-storgia-blurple to-indigo-600 text-white shadow-storgia-blurple/20'
                            : 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-indigo-200'
                            }`}
                    >
                        <span aria-hidden="true">{getInitials(ownerName)}</span>
                        <span className="sr-only">{ownerName}</span>
                    </button>

                    {/* Profile Dropdown */}
                    {isProfileOpen && (
                        <>
                            <button
                                type="button"
                                className="fixed inset-0 z-40 cursor-default"
                                onClick={() => setIsProfileOpen(false)}
                                aria-label="Close profile menu"
                            />
                            <div className={`absolute right-0 mt-3 w-56 rounded-2xl shadow-2xl border z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 ${theme === 'dark'
                                ? 'bg-storgia-dark border-storgia-light-blurple/20'
                                : 'bg-white border-gray-100'
                                }`}>
                                <div className={`px-4 py-3 border-b ${theme === 'dark' ? 'border-storgia-light-blurple/10 bg-storgia-blurple/5' : 'border-gray-50 bg-gray-50/50'}`}>
                                    <p className={`text-sm font-bold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{ownerName}</p>
                                    <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {companyName || 'View Profile'}
                                        {userRole && <span className="opacity-75"> • {userRole.replace('_', ' ')}</span>}
                                    </p>
                                </div>
                                <div className="p-2">
                                    <button
                                        onClick={() => {
                                            navigate('/profile');
                                            setIsProfileOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${theme === 'dark'
                                            ? 'text-gray-300 hover:bg-storgia-light-blurple/10 hover:text-white'
                                            : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600'
                                            }`}
                                    >
                                        <User className="h-4 w-4" />
                                        Profile Settings
                                    </button>
                                    <button
                                        onClick={() => {
                                            onLogout();
                                            setIsProfileOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${theme === 'dark'
                                            ? 'text-storgia-pink hover:bg-storgia-pink/10'
                                            : 'text-red-600 hover:bg-red-50'
                                            }`}
                                    >
                                        <LogOut className="h-4 w-4" />
                                        Sign Out
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
};
